import { IEmploymentAndBankDetails } from 'API';
import * as yup from 'yup';
import {
  textTooLong,
  onlyNumbers,
  lettersOrNumbersNoSpecialChars,
  lettersOrNumbersWithExtraChars,
  lettersOrNumbersNoSpecialCharsMessage,
  requiredMessage,
  onlyNumbersMessage,
  phoneNumberMessage,
  phoneNumberNotRequired,
} from 'Regex';
import { bankAccountValidation } from 'Utils/bankAccountValidation';
import { store } from 'Store';
import { Dictionary } from 'Dictionary';
import { DICTIONARY, EmploymentAndBankDetails } from 'Enum';

export const employmentAndBankDetailSchema: yup.SchemaOf<IEmploymentAndBankDetails> =
  yup.object().shape({
    bankAccountDetailsSection: yup.object({
      accountNumber: yup
        .string()
        .required(requiredMessage)
        .nullable()
        .max(11, textTooLong(11))
        .matches(onlyNumbers, onlyNumbersMessage)
        .test(
          'bankAccountValidation',
          Dictionary[DICTIONARY.EMPLOYMENT_AND_BANK_DETAILS]
            .accountNumberErrorMsg,
          (val) =>
            bankAccountValidation(
              store.getState().employmentAndBankDetails.bankNumber,
              store.getState().employmentAndBankDetails.branchNumber,
              parseInt(val!)
            )
        ),
      bankRelationId: yup.string().required(requiredMessage).nullable(),
      branchRelationId: yup.string().nullable().required(requiredMessage),
    }),
    workPlaceDetailsSection: yup.object({
      employmentStatusId: yup.number().required(requiredMessage).nullable(),
      isMinistryOfDefenseEmployee: yup.boolean().when('employmentStatusId', {
        is: EmploymentAndBankDetails.haveJob,
        then: yup.boolean().required(requiredMessage).nullable(),
        otherwise: yup.boolean().notRequired().nullable(),
      }),
      hasNoLandlinePhone: yup.boolean().notRequired().nullable(),
      hasNoManagerPhone: yup.boolean().notRequired().nullable(),
      jobTitle: yup
        .string()
        .when(['employmentStatusId', 'isMinistryOfDefenseEmployee'], {
          is: (employmentStatusId: any, isMinistryOfDefenseEmployee: boolean) =>
            employmentStatusId &&
            employmentStatusId != EmploymentAndBankDetails.neverWorked &&
            isMinistryOfDefenseEmployee === false,
          then: yup.string().required(requiredMessage).nullable(),
          otherwise: yup.string().notRequired().nullable(),
        })
        .max(500, textTooLong(500)),
      landlinePhoneNum: yup
        .string()
        .matches(phoneNumberNotRequired, phoneNumberMessage)
        .when(
          [
            'employmentStatusId',
            'hasNoLandlinePhone',
            'isMinistryOfDefenseEmployee',
          ],
          {
            is: (
              employmentStatusId: any,
              hasNoLandlinePhone: boolean,
              isMinistryOfDefenseEmployee: boolean
            ) =>
              hasNoLandlinePhone === false &&
              employmentStatusId &&
              employmentStatusId != EmploymentAndBankDetails.neverWorked &&
              isMinistryOfDefenseEmployee === false,
            then: yup.string().required(requiredMessage),
            otherwise: yup.string().notRequired().nullable(),
          }
        ),
      landlinePrefixRelationId: yup
        .string()
        .when(
          [
            'employmentStatusId',
            'hasNoLandlinePhone',
            'isMinistryOfDefenseEmployee',
          ],
          {
            is: (
              employmentStatusId: any,
              hasNoLandlinePhone: boolean,
              isMinistryOfDefenseEmployee: boolean
            ) =>
              hasNoLandlinePhone === false &&
              employmentStatusId &&
              employmentStatusId != EmploymentAndBankDetails.neverWorked &&
              isMinistryOfDefenseEmployee === false,
            then: yup.string().required(requiredMessage).nullable(),
            otherwise: yup.string().notRequired().nullable(),
          }
        ),
      managerName: yup
        .string()
        .when(['employmentStatusId', 'isMinistryOfDefenseEmployee'], {
          is: (employmentStatusId: any, isMinistryOfDefenseEmployee: boolean) =>
            employmentStatusId &&
            employmentStatusId != EmploymentAndBankDetails.neverWorked &&
            isMinistryOfDefenseEmployee === false,
          then: yup.string().required(requiredMessage).nullable(),
          otherwise: yup.string().notRequired().nullable(),
        })
        .max(50, textTooLong(50))
        .matches(
          lettersOrNumbersNoSpecialChars,
          lettersOrNumbersNoSpecialCharsMessage
        ),
      managerPhoneNum: yup
        .string()
        .matches(phoneNumberNotRequired, phoneNumberMessage)
        .when(
          [
            'employmentStatusId',
            'hasNoLandlinePhone',
            'isMinistryOfDefenseEmployee',
          ],
          {
            is: (
              employmentStatusId: any,
              hasNoLandlinePhone: boolean,
              isMinistryOfDefenseEmployee: boolean
            ) =>
              hasNoLandlinePhone === false &&
              employmentStatusId &&
              employmentStatusId != EmploymentAndBankDetails.neverWorked &&
              isMinistryOfDefenseEmployee === false,
            then: yup.string().required(requiredMessage).nullable(),
            otherwise: yup.string().notRequired().nullable(),
          }
        ),
      managerPhonePrefixRelationId: yup
        .string()
        .when(
          [
            'employmentStatusId',
            'hasNoLandlinePhone',
            'isMinistryOfDefenseEmployee',
          ],
          {
            is: (
              employmentStatusId: any,
              hasNoLandlinePhone: boolean,
              isMinistryOfDefenseEmployee: boolean
            ) =>
              hasNoLandlinePhone === false &&
              employmentStatusId &&
              employmentStatusId != EmploymentAndBankDetails.neverWorked &&
              isMinistryOfDefenseEmployee === false,
            then: yup.string().required(requiredMessage).nullable(),
            otherwise: yup.string().notRequired().nullable(),
          }
        ),
      workName: yup
        .string()
        .when(['employmentStatusId', 'isMinistryOfDefenseEmployee'], {
          is: (employmentStatusId: any, isMinistryOfDefenseEmployee: boolean) =>
            employmentStatusId &&
            employmentStatusId != EmploymentAndBankDetails.neverWorked &&
            isMinistryOfDefenseEmployee === false,
          then: yup.string().required(requiredMessage).nullable(),
          otherwise: yup.string().notRequired().nullable(),
        })
        .max(50, textTooLong(50))
        .matches(
          lettersOrNumbersNoSpecialChars,
          lettersOrNumbersNoSpecialCharsMessage
        ),
      workAddress: yup
        .string()
        .when(['employmentStatusId', 'isMinistryOfDefenseEmployee'], {
          is: (employmentStatusId: any, isMinistryOfDefenseEmployee: boolean) =>
            employmentStatusId &&
            employmentStatusId != EmploymentAndBankDetails.neverWorked &&
            isMinistryOfDefenseEmployee === false,
          then: yup.string().required(requiredMessage).nullable(),
          otherwise: yup.string().notRequired().nullable(),
        })
        .max(50, textTooLong(50))
        .matches(
          lettersOrNumbersNoSpecialChars,
          lettersOrNumbersNoSpecialCharsMessage
        ),
    }),
  });
